import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../context/AuthContext'

// ── Tier colour tokens ────────────────────────────────────────────────────────
const TIERS = [
  { id: 'bronze',   label: 'Bronze',   emoji: '🥉', color: '#92400e', bg: '#fef3c7', dot: '#d97706', border: '#fde68a' },
  { id: 'silver',   label: 'Silver',   emoji: '🥈', color: '#475569', bg: '#f1f5f9', dot: '#94a3b8', border: '#e2e8f0' },
  { id: 'gold',     label: 'Gold',     emoji: '🥇', color: '#713f12', bg: '#fefce8', dot: '#eab308', border: '#fde047' },
  { id: 'platinum', label: 'Platinum', emoji: '💎', color: '#3730a3', bg: '#eef2ff', dot: '#6366f1', border: '#c7d2fe' },
]
const tierMap = Object.fromEntries(TIERS.map(t => [t.id, t]))

// Derive effective tier from live settings-config thresholds — views only
// (a creator has no revenue/orders metric, just audience size)
const computeTier = (views, cfg) => {
  const v = Number(views) || 0
  if (v >= (cfg.creator_tier_platinum_min_views || 100000)) return 'platinum'
  if (v >= (cfg.creator_tier_gold_min_views     || 25000))  return 'gold'
  if (v >= (cfg.creator_tier_silver_min_views   || 5000))   return 'silver'
  return 'bronze'
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const rwf      = (n) => n != null ? `RWF ${Number(n).toLocaleString()}` : '—'
const views    = (n) => n != null ? Number(n).toLocaleString() : '0'
const avatarBg = (str = '') => {
  const c = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0; for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return c[Math.abs(h) % c.length]
}
const initials = (name = '') => name.trim().charAt(0).toUpperCase() || '?'

// ── TierBadge ─────────────────────────────────────────────────────────────────
function TierBadge({ tierId, isOverride }) {
  const t = tierMap[tierId] || tierMap.bronze
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: t.bg, color: t.color, border: `1px solid ${t.border}`, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: t.dot, flexShrink: 0 }} />
      {t.emoji} {t.label}{isOverride ? ' ✎' : ''}
    </span>
  )
}

// ── StatCard ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, accent, sub }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '18px 20px', flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', color: accent || '#111317' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 4, fontFamily: "'JetBrains Mono',monospace" }}>{sub}</div>}
    </div>
  )
}

// ── Tier Summary Card ─────────────────────────────────────────────────────────
function TierSummaryCard({ tier, count, cfg }) {
  const t = tierMap[tier.id]
  const minViews = cfg[`creator_tier_${tier.id}_min_views`]
  const disc     = cfg[`creator_tier_${tier.id}_commission_discount`]
  return (
    <div style={{ background: '#fff', border: `1px solid ${t.border}`, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ background: t.bg, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 22 }}>{tier.emoji}</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: t.color }}>{tier.label}</div>
          <div style={{ fontSize: 10, color: t.color, opacity: 0.7, fontFamily: "'JetBrains Mono',monospace" }}>{count} creator{count !== 1 ? 's' : ''}</div>
        </div>
      </div>
      <div style={{ padding: '14px 18px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[
            ['Min Views',    tier.id === 'bronze' ? 'Any' : views(minViews)],
            ['Commission −', tier.id === 'bronze' ? '0%' : `${((disc ?? 0) * 100).toFixed(0)}%`],
          ].map(([k, v]) => (
            <div key={k} style={{ background: '#f9f9f7', borderRadius: 7, padding: '8px 10px' }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', marginBottom: 3 }}>{k}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#111317' }}>{v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Override Tier Modal ───────────────────────────────────────────────────────
function OverrideModal({ creator, currentTier, onClose, onSave }) {
  const [selected, setSelected] = useState(currentTier)
  const [reason,   setReason]   = useState('')
  const [saving,   setSaving]   = useState(false)
  const [error,    setError]    = useState('')

  const handleSave = async () => {
    if (!reason.trim()) { setError('Please enter a reason for the override.'); return }
    setSaving(true)
    try {
      await api.put(`/tiers/admin/creators/${creator.creator_id}/override`, { tier: selected, reason })
    } catch { /* optimistic */ }
    onSave(creator.creator_id, selected)
    onClose()
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 400 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 430, background: '#fff', borderRadius: 14, zIndex: 401, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn{from{opacity:0;transform:translate(-50%,-48%) scale(0.97)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>
        <div style={{ padding: '22px 26px', borderBottom: '1px solid #e9e9e4' }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>Override Tier</div>
          <div style={{ fontSize: 12, color: '#9a9a94', marginTop: 2 }}>{creator.display_name} · current: <b>{currentTier}</b></div>
        </div>
        <div style={{ padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#111317', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8, fontFamily: "'JetBrains Mono',monospace" }}>Select Tier</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {TIERS.map(t => (
                <div key={t.id} onClick={() => setSelected(t.id)}
                  style={{ padding: '10px 12px', borderRadius: 8, border: `2px solid ${selected === t.id ? t.dot : '#e9e9e4'}`, background: selected === t.id ? t.bg : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.12s' }}>
                  <span style={{ fontSize: 18 }}>{t.emoji}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: selected === t.id ? t.color : '#111317' }}>{t.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#111317', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, fontFamily: "'JetBrains Mono',monospace" }}>Reason (required)</div>
            <textarea value={reason} onChange={e => { setReason(e.target.value); setError('') }} placeholder="e.g. Promotional partnership, exceptional performance…" rows={3}
              style={{ width: '100%', borderRadius: 8, border: `1px solid ${error ? '#fca5a5' : '#e9e9e4'}`, padding: '10px 12px', fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: "'Plus Jakarta Sans',sans-serif", boxSizing: 'border-box' }} />
            {error && <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4 }}>{error}</div>}
          </div>
        </div>
        <div style={{ padding: '0 26px 22px', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, height: 40, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 1, height: 40, borderRadius: 8, border: 'none', background: saving ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {saving ? 'Saving…' : 'Apply Override'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── PagBtn ────────────────────────────────────────────────────────────────────
function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ minWidth: 34, height: 34, borderRadius: 7, border: '1px solid #e9e9e4', background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#9a9a94' : '#111317', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', padding: '0 10px', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: disabled ? 0.4 : 1 }}>
      {label}
    </button>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function CreatorTiers() {
  const [creators,    setCreators]    = useState([])
  const [loading,     setLoading]     = useState(false)
  const [cfgLoading,  setCfgLoading]  = useState(true)
  const [cfg,         setCfg]         = useState({})
  const [searchInput, setSearchInput] = useState('')
  const [search,      setSearch]      = useState('')
  const [tierFilter,  setTierFilter]  = useState('')
  const [page,        setPage]        = useState(1)
  const [totalPages,  setTotalPages]  = useState(1)
  const [overrides,   setOverrides]   = useState({})
  const [overriding,  setOverriding]  = useState(null)
  const fetchRef = useRef(0)
  const PER_PAGE = 20

  // Load tier thresholds from the shared settings config
  useEffect(() => {
    setCfgLoading(true)
    api.get('/settings/admin/config')
      .then(res => setCfg(res.data.config || {}))
      .catch(() => {})
      .finally(() => setCfgLoading(false))
  }, [])

  // Also load any manual overrides that were previously saved
  useEffect(() => {
    api.get('/tiers/admin/creators/overrides')
      .then(res => {
        const map = {}
        ;(res.data.overrides || []).forEach(o => { map[o.creator_id] = o.tier })
        setOverrides(map)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => { setPage(1) }, [search, tierFilter])

  const fetchCreators = useCallback(async () => {
    const id = ++fetchRef.current
    setLoading(true)
    try {
      const params = new URLSearchParams({ page, limit: PER_PAGE, ...(search && { search }), status: 1 })
      const res = await api.get(`/creators/admin/list?${params}`)
      if (id !== fetchRef.current) return
      setCreators(res.data.creators || [])
      setTotalPages(res.data.totalPages || 1)
    } catch {
      if (id !== fetchRef.current) return
      setCreators([])
      setTotalPages(1)
    } finally {
      if (id === fetchRef.current) setLoading(false)
    }
  }, [page, search])

  useEffect(() => { fetchCreators() }, [fetchCreators])

  const handleOverrideSave = (creatorId, tierId) => {
    setOverrides(prev => {
      const next = { ...prev }
      if (tierId === null) delete next[creatorId]
      else next[creatorId] = tierId
      return next
    })
  }

  const enriched = creators.map(c => ({
    ...c,
    computed_tier:  computeTier(c.cumulative_views, cfg),
    effective_tier: overrides[c.creator_id] || computeTier(c.cumulative_views, cfg),
    is_override:    !!overrides[c.creator_id],
  }))

  const displayed = tierFilter ? enriched.filter(c => c.effective_tier === tierFilter) : enriched

  const tierCounts = TIERS.reduce((acc, t) => {
    acc[t.id] = enriched.filter(c => c.effective_tier === t.id).length
    return acc
  }, {})

  const selectStyle = { border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Creator Tier Management</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Track creator progression across Bronze → Platinum, based on cumulative live-stream views. Thresholds are configured in Settings.</div>
        </div>
        <a href="/settings" style={{ height: 38, padding: '0 16px', borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', color: '#111317', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
          ⚙️ Edit Thresholds
        </a>
      </div>

      {/* Tier distribution stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 28 }}>
        {TIERS.map(t => (
          <StatCard key={t.id} label={`${t.emoji} ${t.label}`} value={tierCounts[t.id] ?? 0} accent={t.dot}
            sub={t.id !== 'bronze' ? `≥ ${views(cfg[`creator_tier_${t.id}_min_views`])} views` : 'Default tier'} />
        ))}
      </div>

      {/* Tier summary cards with live thresholds */}
      {!cfgLoading && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Current Tier Configuration <span style={{ fontSize: 11, color: '#9a9a94', fontWeight: 500 }}>(edit in Settings → Creator Tier Thresholds)</span></div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
            {TIERS.map(t => (
              <TierSummaryCard key={t.id} tier={t} count={tierCounts[t.id] ?? 0} cfg={cfg} />
            ))}
          </div>
        </div>
      )}

      {/* Creator list */}
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Creator Tier Assignments</div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 12px' }}>
          <span style={{ fontSize: 14, color: '#9a9a94' }}>🔍</span>
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search creator name, owner, phone…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111317', flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
          {searchInput && <span onClick={() => { setSearchInput(''); setSearch('') }} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 16 }}>×</span>}
        </div>
        <select value={tierFilter} onChange={e => setTierFilter(e.target.value)} style={selectStyle}>
          <option value="">All Tiers</option>
          {TIERS.map(t => <option key={t.id} value={t.id}>{t.emoji} {t.label}</option>)}
        </select>
        <button onClick={fetchCreators} style={{ height: 36, width: 36, borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↻</button>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2.2fr 1.3fr 1.1fr 1.1fr 1.1fr 1.1fr 1.2fr', padding: '10px 20px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
          {['Creator','Category','Views','Total Tips','Computed Tier','Effective Tier','Actions'].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading creators…</div>
        ) : displayed.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No creators found.</div>
        ) : displayed.map((c, i) => (
          <div key={c.creator_id}
            style={{ display: 'grid', gridTemplateColumns: '2.2fr 1.3fr 1.1fr 1.1fr 1.1fr 1.1fr 1.2fr', padding: '13px 20px', borderBottom: i < displayed.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: avatarBg(c.display_name), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#111317', flexShrink: 0 }}>
                {initials(c.display_name)}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{c.display_name}</div>
                <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{c.owner_name || c.owner_phone || '—'}</div>
              </div>
            </div>

            <div style={{ fontSize: 12, color: '#6b7280' }}>{c.category || '—'}</div>
            <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, color: '#1d4ed8' }}>{views(c.cumulative_views)}</div>
            <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, color: '#15803d' }}>{rwf(c.total_tips)}</div>
            <TierBadge tierId={c.computed_tier} />
            <TierBadge tierId={c.effective_tier} isOverride={c.is_override} />

            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setOverriding(c)}
                style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                ✎ Override
              </button>
              {c.is_override && (
                <button onClick={() => handleOverrideSave(c.creator_id, null)}
                  style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #fecaca', background: '#fff5f5', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  Reset
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>Page {page} of {totalPages}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
            })}
            <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}

      {overriding && (
        <OverrideModal
          creator={overriding}
          currentTier={overrides[overriding.creator_id] || computeTier(overriding.cumulative_views, cfg)}
          onClose={() => setOverriding(null)}
          onSave={handleOverrideSave}
        />
      )}
    </div>
  )
}
